import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { useGlobalModalContext } from "components/context/GlobalModalContext";
import { PartyPopper, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";
import { useGetDocument, useListDocuments } from "services/documents";
import {
  GetDocumentRequest,
  ListDocumentsRequest,
} from "protogen/documents_service_pb";
import { DocumentType } from "protogen/documents_pb";
import { PROD_ONBOARDING_AVAILABILITY_FORM_TITLE } from "components/documents/constants";

export default ({
  urlParamName,
  textMessage,
  familyRef,
}: {
  urlParamName: string;
  textMessage: string;
  familyRef: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasParam = searchParams.get(urlParamName) === "true";
  const [dismissed, setDismissed] = useState(false);
  const { setModalOpen } = useGlobalModalContext();
  const [themeBase] = useState(
    document.querySelector("meta[name=theme-color]")?.getAttribute("content") ||
      undefined,
  );
  const isMobile = useIsMobile();
  const [availabilityText, setAvailabilityText] = useState<string | null>(null);

  const { request: listDocuments, loading: listLoading } = useListDocuments();
  const { request: getDocument, loading: getLoading } = useGetDocument();

  const revertThemeColor = () => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", themeBase || "");
    }
  };

  useEffect(() => {
    setModalOpen(!dismissed);
    return () => setModalOpen(false);
  }, [dismissed, setModalOpen]);

  const loadAvailabilityText = async () => {
    const resp = await listDocuments(
      new ListDocumentsRequest({
        familyRefs: [familyRef],
        documentTypes: [DocumentType.NOTE],
      }),
    );
    if (resp?.documents) {
      const availabilityDoc = resp.documents.find(
        (doc) =>
          doc.title?.toLowerCase() ===
          PROD_ONBOARDING_AVAILABILITY_FORM_TITLE.toLocaleLowerCase(),
      );
      if (availabilityDoc?.uuid) {
        const documentResp = await getDocument(
          new GetDocumentRequest({
            documentRef: availabilityDoc.ref,
            documentType: DocumentType.FORM,
          }),
        );
        if (documentResp?.document?.note?.content?.payload) {
          const doc = JSON.parse(
            documentResp?.document?.note?.content?.payload,
          );
          setAvailabilityText(doc.fields[0].value);
        }
      }
    }
  };

  useEffect(() => {
    if (hasParam && !dismissed) {
      loadAvailabilityText();
    }
  }, [hasParam, dismissed]);

  const sendWelcomeText = () => {
    setDismissed(true);
    setSearchParams((p) => {
      p.set("textMessage", textMessage);
      return p;
    });
  };

  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (!dismissed && hasParam) {
      if (metaThemeColor) {
        // Note(Kip) Not finalized.
        metaThemeColor.setAttribute("content", "#575F70");
      }
    } else {
      revertThemeColor();
    }
    return () => {
      revertThemeColor();
    };
  }, [hasParam, dismissed]);
  if (!hasParam) return null;

  return (
    <Dialog
      open={!dismissed}
      PaperComponent={Paper}
      PaperProps={{ style: { borderRadius: "16px" } }}
      disableEscapeKeyDown={true}
      onClose={(_, reason) => {
        if (reason && reason === "backdropClick") return;
        setDismissed(true);
      }}
      sx={{ "& .MuiDialog-paper": { maxWidth: "600px", margin: "16px" } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          right: "16px",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            setDismissed(true);
          }}
        >
          <X color="#8E9598" />
        </IconButton>
      </Box>

      <FlexBox
        variant="contained"
        size="large"
        direction="column"
        sx={{ border: "none" }}
      >
        <PartyPopper color="#198282" size={48} />
        <Typography variant="h3">
          Schedule your client onboarding call{" "}
        </Typography>
        <Typography variant="body">
          Your new client is ready to connect! Introduce yourself and set up an
          onboarding call to kick off their Faye experience.
        </Typography>

        <FlexBox
          variant="contained"
          direction="column"
          gap="4px"
          sx={{
            backgroundColor: "teal.1",
            border: "none",
            padding: "12px 16px",
          }}
        >
          <Typography variant="h5">Client Availability</Typography>
          <Typography variant="bodySmall">
            {!(listLoading || getLoading) ? (
              availabilityText ||
              "Please reach out to client to request their availability for an onboarding call."
            ) : (
              <Skeleton variant="text" width="100%" height="100px" />
            )}
          </Typography>
        </FlexBox>
        <Typography variant="body">
          Please reach out to{" "}
          <Link href="https://advisor.findfaye.com/inbox/messages/AAAAAAAAKwU%3D">
            Loren
          </Link>{" "}
          or{" "}
          <Link href="https://advisor.findfaye.com/inbox/messages/AAAAAAAFt2c%3D">
            Jackie
          </Link>{" "}
          for any questions. We are here and happy to help!
        </Typography>
      </FlexBox>

      <Divider />

      <FlexBox variant="contained" direction="column" sx={{ border: "none" }}>
        <FlexBox justifyContent="end" sx={{ width: "100%" }}>
          <Button
            variant="text"
            sx={{ flex: isMobile ? 1 : "initial" }}
            onClick={() => setDismissed(true)}
          >
            Close
          </Button>
          <Button variant="contained" onClick={sendWelcomeText}>
            Send a "welcome" text
          </Button>
        </FlexBox>
      </FlexBox>
    </Dialog>
  );
};
